import { useEffect, useMemo } from 'react'
import { GetStaticPaths, NextPage } from 'next'
import { NextSeo } from 'next-seo'
import { useSelector } from 'react-redux'
import { olympiadsSelectors } from '@ucheba/store/institutions/olympiads'
import { institutionItemSelectors } from '@ucheba/store/institutions/item'
import { setParams } from '@ucheba/utils/helpers/analytics/ga'
import { EActivityTypes } from '@ucheba/utils/types/userActivityTypes'
import { coreActions } from '@ucheba/store/core'
import { IUzPageProps } from '../../../../../components/LayoutInstitution/types'
import Custom404 from '../../../../404.page'
import { useUserActivity } from '../../../../../../utils/hooks/useUserActivity'
import Page from '../../../ssr/[id]/olympiads/desktop'
import { revalidate } from '../../../constants'
import { wrapper } from '../../../../../store/store'
import { useUzCanonical } from '../../../ssr/[id]/utils/useUzCanonical'
import { getInstitutionOlympiadsPageProps } from '../../../bll/getInstitutionOlympiadsPageProps'

const InstitutionOlympiadsPage: NextPage<IUzPageProps> = (props) => {
  const { content, notFound } = props
  const currentInstitution = useSelector(institutionItemSelectors.currentInstitution)
  const { canonical } = useUzCanonical()

  const params = useMemo(() => {
    const obj: any = {}

    if (currentInstitution) {
      obj.dimension1 = currentInstitution.parent?.id || currentInstitution.id

      if (currentInstitution.parent?.id) {
        obj.dimension2 = currentInstitution.id
      }
    }

    return obj
  }, [currentInstitution])

  setParams(params)

  const { total } = useSelector(olympiadsSelectors.entity)

  const { setUserActivity } = useUserActivity()

  useEffect(() => {
    const { href } = window.location

    if (currentInstitution) {
      setUserActivity({
        activityType: EActivityTypes.viewing_institution_info,
        institutionId: currentInstitution.id,
        data: { url: href },
      })
    }
  }, [])

  if (notFound) {
    return <Custom404 />
  }

  return (
    <>
      <NextSeo
        title={content.seo.meta.title}
        description={content.seo.meta.description}
        noindex={!total}
        nofollow={!total}
        canonical={canonical}
        additionalMetaTags={[
          {
            property: 'keywords',
            content: content.seo.meta.keywords,
          },
        ]}
      />

      <Page {...(props as any)} />
    </>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps = wrapper.getStaticProps(
  (store) =>
    async (ctx): Promise<any> => {
      const { dispatch } = store

      const { notFound, content } = await getInstitutionOlympiadsPageProps({ ctx, store })

      if (notFound) {
        return {
          notFound: true,
          revalidate,
        }
      }

      dispatch(coreActions.toggleTouch(false))

      return {
        props: {
          content,
        },
        revalidate,
      }
    }
)

export default InstitutionOlympiadsPage
